import posthog from "posthog-js"
import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "mojo.seoLeadCapture.state"

// Connects to data-controller="seo-lead-capture"
export default class extends Controller {
  static targets = ["submitButton"]
  static values = {
    eventProperties: Object
  }

  connect() {
    this.thresholdPx = this.calculateThreshold()
    this.triggered = false
    this.triggerScrollY = null

    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)

    this.track("seo_lead_capture_eligible_view")

    if (this.suppressed()) return

    window.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("resize", this.handleResize)
  }

  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleResize)
  }

  dismiss() {
    this.persistState("dismissed")
    this.track("seo_lead_capture_dismissed")
    this.teardown()
    this.element.remove()
  }

  submitStart() {
    this.track("seo_lead_capture_submit_clicked")

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.setAttribute("aria-busy", "true")
    }
  }

  submitEnd(event) {
    const responseStatus = event.detail?.fetchResponse?.response?.status

    if (event.detail.success) {
      this.persistState("submitted")
      this.track("seo_lead_capture_submit_succeeded", { response_status: responseStatus })
      return
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.removeAttribute("aria-busy")
    }

    this.track("seo_lead_capture_submit_failed", { response_status: responseStatus })
  }

  handleScroll() {
    if (this.triggered || this.suppressed()) return
    if (window.scrollY < this.thresholdPx) return

    this.triggered = true
    this.triggerScrollY = Math.round(window.scrollY)

    this.element.classList.remove("pointer-events-none", "opacity-0", "translate-y-4")
    this.element.classList.add("pointer-events-auto", "opacity-100", "translate-y-0")

    this.track("seo_lead_capture_impression")
    this.teardown()
  }

  handleResize() {
    this.thresholdPx = this.calculateThreshold()
  }

  calculateThreshold() {
    return Math.round(window.innerHeight)
  }

  suppressed() {
    const state = window.localStorage.getItem(STORAGE_KEY)
    return state === "dismissed" || state === "submitted"
  }

  persistState(state) {
    window.localStorage.setItem(STORAGE_KEY, state)
  }

  teardown() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleResize)
  }

  track(eventName, extraProperties = {}) {
    if (!posthog.__loaded) return

    posthog.capture(eventName, {
      ...this.baseProperties(),
      ...extraProperties
    })
  }

  baseProperties() {
    return {
      ...(this.eventPropertiesValue || {}),
      device_type: this.deviceType(),
      scroll_threshold_px: this.thresholdPx,
      scroll_y_at_trigger: this.triggerScrollY
    }
  }

  deviceType() {
    if (window.innerWidth < 640) return "mobile"
    if (window.innerWidth < 1024) return "tablet"

    return "desktop"
  }
}
